import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Globe, Cpu, Eye, Settings2, Loader2, Check,
  Bell, List, Map, Type, Target, Zap,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { FieldLabel } from '../../components/FieldLabel'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { MultiSelect } from '@/components/ui/multi-select'
import { CurrencySelect } from '../../components/CurrencySelect'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { useAvailableCurrencies } from '../../hooks/useAvailableCurrencies'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { useNetwork } from '../../contexts/NetworkContext'
import { useNetworkProfile } from '../../contexts/NetworkProfileContext'
import { NetworksApi, NetworkProfilesApi, NetworkProfile, NetworkInput, Network } from '@/services/networks/networksApi'
import { BID_STRATEGIES, type BidStrategy } from '@/services/lineItems/lineItemsApi'
import {
  BID_FIELDS,
  MARKETPLACE_BID_FIELDS,
  OBJECTIVE_TYPES,
  defaultBidStrategyOf,
  enabledFields,
  parseAdnDefaults,
  type ObjectiveType,
} from '@/services/networks/networkFieldSettings'
import { apiErrorDescription } from '@/services/core/apiError'
import { CustomEventTypesApi } from '@/services/networks/customEventTypesApi'
import { biddingCustomEventIds } from '@/services/networks/networkFieldSettings'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { FeeTemplatesCard } from '@/components/FeeTemplatesCard'
import { useCurrentUser } from '@/contexts/UserContext'
import { isSuperAdmin } from '@/data/superAdmins'
import {
  draftToFeeTemplate,
  feeTemplateToDraft,
  parseFeeTemplates,
} from '@/services/users/teamFees'

// ─── Constants ────────────────────────────────────────────────────────────────



const VIEWABILITY_METHODS = [
  { value: 'PER_IMPRESSION', label: 'Per impression' },
  { value: 'PER_RENDERED', label: 'Per rendered' },
]
const ECPM_METHODS = [
  { value: 'PER_IMPRESSION', label: 'Per impression' },
  { value: 'PER_RENDERED', label: 'Per rendered' },
]
const CPM_COST_MODELS = [
  { value: 'PER_IMPRESSION', label: 'Per impression' },
  { value: 'PER_RENDERED', label: 'Per rendered' },
]

const PAYMENT_PROVIDERS = [
  { value: 'NONE', label: 'None' },
  { value: 'BNP_PARIBAS_SANDBOX', label: 'BNP Paribas (Sandbox)' },
  { value: 'BNP_PARIBAS', label: 'BNP Paribas' },
  { value: 'STRIPE_SANDBOX', label: 'Stripe (Sandbox)' },
  { value: 'STRIPE', label: 'Stripe' },
]
const LINE_ITEM_STATES = [
  { value: '',          label: 'No value' },
  { value: 'PROPOSED',  label: 'Proposed' },
  { value: 'RESERVED',  label: 'Reserved' },
  { value: 'APPROVED',  label: 'Approved' },
  { value: 'PAUSED',    label: 'Paused' },
  { value: 'STOPPED',   label: 'Stopped' },
]
const SMOOTHING_OPTIONS = [
  { value: 'EVEN', label: 'Even delivery' },
  { value: 'FRONTLOADED', label: 'Frontloaded delivery' },
  { value: 'FASTASPOSSIBLE', label: 'As fast as possible' },
]
const CALENDAR_START_DAYS = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
]
const CALENDAR_DAY_NUMBER: Record<string, number> = {
  MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4, FRIDAY: 5, SATURDAY: 6, SUNDAY: 7,
}
const CALENDAR_NUMBER_DAY: Record<number, string> = {
  1: 'MONDAY', 2: 'TUESDAY', 3: 'WEDNESDAY', 4: 'THURSDAY', 5: 'FRIDAY', 6: 'SATURDAY', 7: 'SUNDAY',
}
// Field lists live with the line item page that consumes them, so the two can't drift.
const OBJECTIVE_FIELD_LABELS: Record<ObjectiveType, string> = {
  IMPRESSION: 'Impressions',
  RENDERED_IMPRESSION: 'Rendered Impressions',
  VISIBLE_IMPRESSION: 'Visible Impression',
  VIEWABLE_IMPRESSION: 'Viewable Impression',
  CLICK: 'Click',
  CONVERSION: 'Conversion',
  BUDGET: 'Budget',
}
const BID_SPEC_FIELD_OPTIONS = BID_FIELDS.map((value) => ({ value, label: value.toUpperCase() }))
const MARKETPLACE_BID_SPEC_FIELD_OPTIONS = MARKETPLACE_BID_FIELDS.map((value) => ({
  value,
  label: value.toUpperCase(),
}))
const OBJECTIVE_FIELD_OPTIONS = OBJECTIVE_TYPES.map((value) => ({
  value,
  label: OBJECTIVE_FIELD_LABELS[value],
}))

const parseObjectiveFields = enabledFields

function toObjectiveFieldsObj(selected: string[]): Record<string, boolean> {
  return Object.fromEntries(OBJECTIVE_FIELD_OPTIONS.map(({ value }) => [value, selected.includes(value)]))
}

function toBidSpecObj(selected: string[], options: { value: string }[]): Record<string, boolean> {
  return Object.fromEntries(options.map(({ value }) => [value, selected.includes(value)]))
}
const RATE_LIMIT_TRANSLATIONS = [
  { value: '1', label: 'Original Rate Limit Naming' },
  { value: '2', label: 'Caps' },
  { value: '3', label: 'Frequency' },
]

const RATE_LIMIT_SCOPES = [
  { value: 'PER_USER', label: 'Per user' },
  { value: 'PER_DEVICE', label: 'Per device' },
]
const MARKETPLACE_TARGETING_OPTIONS = [
  { value: 'FOLLOW_TARGETING_RULES', label: 'Follow targeting rules' },
  { value: 'IGNORE_TARGETING_RULES', label: 'Ignore targeting rules' },
]
const CREATIVE_DELIVERY_OPTIONS = [
  { value: '', label: 'No value' },
  { value: 'NONE', label: 'Open' },
  { value: 'CREATIVE', label: 'Unique' },
  { value: 'LINE_ITEM', label: 'One per Line Item' },
]

// Maps our internal FIELD_NAME_KEYS to the keys used in adnDefaults.translations
const TRANSLATION_KEY_MAP: Record<string, { nameKey: string; abbrKey: string }> = {
  impressions:          { nameKey: 'impression',               abbrKey: 'impressionAbbr' },
  renderedImpressions:  { nameKey: 'rendered',                 abbrKey: 'renderedAbbr' },
  winRate:              { nameKey: 'winRate',                  abbrKey: 'winRateAbbr' },
  impressionsPerUnique: { nameKey: 'impressionsPerUniqueUser', abbrKey: 'impressionsPerUniqueUserAbbr' },
  renderedRate:         { nameKey: 'renderedRate',             abbrKey: 'renderedRateAbbr' },
  earnings:             { nameKey: 'earnings',                 abbrKey: 'earningsAbbr' },
  revenueShare:         { nameKey: 'revenueShare',             abbrKey: 'revenueShareAbbr' },
  fees:                 { nameKey: 'fees',                     abbrKey: 'feesAbbr' },
  techFee:              { nameKey: 'techFee',                  abbrKey: 'techFeeAbbr' },
  creativeTagFee:       { nameKey: 'creativeTagFee',           abbrKey: 'creativeTagFeeAbbr' },
  creativeFormatFee:    { nameKey: 'creativeFormatFee',        abbrKey: 'creativeFormatFeeAbbr' },
  dataFee:              { nameKey: 'dataFee',                  abbrKey: 'dataFeeAbbr' },
  supplementaryFee:     { nameKey: 'supplementaryFee',         abbrKey: 'supplementaryFeeAbbr' },
}

const FIELD_NAME_KEYS: { key: string; label: string }[] = [
  { key: 'impressions',          label: 'Impressions' },
  { key: 'renderedImpressions',  label: 'Rendered Impressions' },
  { key: 'winRate',              label: 'Win Rate' },
  { key: 'impressionsPerUnique', label: 'Impressions per Unique' },
  { key: 'renderedRate',         label: 'Rendered Rate' },
  { key: 'earnings',             label: 'Earnings' },
  { key: 'revenueShare',         label: 'Revenue Share' },
  { key: 'fees',                 label: 'Fees' },
  { key: 'techFee',              label: 'Tech Fee' },
  { key: 'creativeTagFee',       label: 'Creative Tag Fee' },
  { key: 'creativeFormatFee',    label: 'Creative Format Fee' },
  { key: 'dataFee',              label: 'Data Fee' },
  { key: 'supplementaryFee',     label: 'Supplementary Fee' },
]

// ─── Helper ───────────────────────────────────────────────────────────────────

function optionLabel(options: { value: string; label: string }[], value: string) {
  return options.find((o) => o.value === value)?.label ?? value
}

function validateUrl(value: string) {
  if (!value) return undefined
  try {
    const u = new URL(value)
    if (!['https:', 'http:'].includes(u.protocol)) return 'Must be a valid URL (e.g. https://example.com)'
    if (!u.hostname.includes('.')) return 'Must be a valid URL (e.g. https://example.com)'
    return undefined
  } catch { return 'Must be a valid URL (e.g. https://example.com)' }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SwitchRow({ label, checked, onCheckedChange }: { label: string; checked: boolean; onCheckedChange: (v: boolean) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
      <Label style={{ margin: 0 }}>{label}</Label>
    </div>
  )
}

// ─── Form ─────────────────────────────────────────────────────────────────────

function NetworkEditForm({ network, profile }: { network: Network; profile: NetworkProfile }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { setNetworkProfile } = useNetworkProfile()
  const { options: currencyOptions } = useAvailableCurrencies()
  const superAdmin = isSuperAdmin(useCurrentUser()?.username)
  // A fixed fee is quoted in every network currency, the default first.
  const feeCurrencies = [
    network.defaultCurrency,
    ...network.availableCurrencies.filter((c) => c !== network.defaultCurrency),
  ]
  const { data: customEvents = [] } = useQuery({
    queryKey: ['custom-events', ''],
    queryFn: () => CustomEventTypesApi.getAll(),
  })

  const form = useForm({
    defaultValues: {
      // General
      name: network.name ?? '',
      defaultCurrency: network.defaultCurrency ?? 'USD',
      availableCurrencies: network.availableCurrencies ?? [],
      defaultTierId: network.defaultTier?.id ?? '',
      // Appearance
      logoUrl: (profile.profile as Record<string, string> | undefined)?.adnUiLogo ?? network.logoUrl ?? '',
      reportLogoUrl: network.reportLogoUrl ?? '',
      notificationsUrl: network.notificationsUrl ?? '',
      // Logging
      // Calculation methods
      defaultCpmCostModel: network.defaultCpmCostModel ?? 'PER_IMPRESSION',
      eCpmMethod: network.eCpmMethod ?? 'PER_IMPRESSION',
      viewabilityMethod: network.viewabilityMethod ?? 'PER_IMPRESSION',
      // Privacy & data
      requiresUserConsent: network.requiresUserConsent ?? false,
      // Line item defaults
      defaultLineItemState: (parseAdnDefaults(profile.profile).lineItemUserState as string | undefined) ?? network.defaultLineItemState ?? '',
      defaultMarketplaceLineItemState: (parseAdnDefaults(profile.profile).marketplaceLineItemUserState as string | undefined) ?? network.defaultMarketplaceLineItemState ?? '',
      defaultLineItemDateSpan: network.defaultLineItemDateSpan ?? 7,
      defaultCpmAmount: ((parseAdnDefaults(profile.profile).cpm as { amount?: number } | undefined)?.amount ?? network.defaultCpm?.amount) ?? 0,
      defaultCpmCurrency: ((parseAdnDefaults(profile.profile).cpm as { currency?: string } | undefined)?.currency ?? network.defaultCpm?.currency) ?? network.defaultCurrency ?? 'USD',
      hideCpmFees: (() => { const d = parseAdnDefaults(profile.profile); return 'showCpmFees' in d ? !(d.showCpmFees as boolean) : (network.hideCpmFees ?? false) })(),
      hideLineItemExternalRef: (() => { const d = parseAdnDefaults(profile.profile); return 'showExternalReference' in d ? !(d.showExternalReference as boolean) : (network.hideLineItemExternalRef ?? false) })(),
      calendarStartDay: (() => { const d = parseAdnDefaults(profile.profile); const v = d.startDayCalendar as number | undefined; return v != null ? (CALENDAR_NUMBER_DAY[v] ?? 'MONDAY') : (network.calendarStartDay ?? 'MONDAY') })(),
      defaultLineItemSmoothing: (() => { const d = parseAdnDefaults(profile.profile); const v = d.deliveryCurve as string | undefined; return v ? v.replaceAll('_', '') : (network.defaultLineItemSmoothing ?? 'EVEN') })(),
      defaultMarketplaceLineItemSmoothing: (() => { const d = parseAdnDefaults(profile.profile); const v = d.marketplaceDeliveryCurve as string | undefined; return v ? v.replaceAll('_', '') : (network.defaultMarketplaceLineItemSmoothing ?? 'EVEN') })(),
      defaultBidStrategy: defaultBidStrategyOf(profile),
      defaultMarketplaceTargetingOption: (() => { const d = parseAdnDefaults(profile.profile); return 'defaultIgnoreMarketplaceTargeting' in d ? (d.defaultIgnoreMarketplaceTargeting ? 'IGNORE_TARGETING_RULES' : 'FOLLOW_TARGETING_RULES') : (network.defaultMarketplaceTargetingOption ?? 'FOLLOW_TARGETING_RULES') })(),
      defaultCreativeDelivery: (parseAdnDefaults(profile.profile).lineItemDeduplicationLevel as string | undefined) ?? network.defaultCreativeDelivery ?? '',
      defaultRateLimitScope: (parseAdnDefaults(profile.profile).rateLimitScope as string | undefined) ?? network.defaultRateLimitScope ?? 'PER_USER',
      rateLimitAlias: (parseAdnDefaults(profile.profile).rateLimitTranslations as string | undefined) ?? network.rateLimitAlias ?? '',
      hideIabCategory: (() => { const d = parseAdnDefaults(profile.profile); return 'iabCategoryOnLineItem' in d ? (d.iabCategoryOnLineItem as string) === 'HIDE' : (network.hideIabCategory ?? false) })(),
      defaultCreativeName: (parseAdnDefaults(profile.profile).defaultCreativeName as string | undefined) ?? network.defaultCreativeName ?? '',
      allowReachAnalysis: (() => { const d = parseAdnDefaults(profile.profile); const opts = (d.runReportsOptions as string[] | undefined) ?? []; return opts.length ? opts.includes('REACH_ANALYSIS') : (network.allowReachAnalysis ?? false) })(),
      allowAdvertiserReports: (() => { const d = parseAdnDefaults(profile.profile); const opts = (d.runReportsOptions as string[] | undefined) ?? []; return opts.length ? opts.includes('ADVERTISER_REPORTS') : (network.allowAdvertiserReports ?? false) })(),
      appSectionVisibility: (parseAdnDefaults(profile.profile).appSectionVisibility as string | undefined) === 'SHOW_ALL',
      // Objectives
      objectiveFields: parseObjectiveFields(parseAdnDefaults(profile.profile).objectiveFields),
      marketplaceObjectiveFields: parseObjectiveFields(parseAdnDefaults(profile.profile).marketplaceObjectiveFields),
      rateLimitFields: parseObjectiveFields(parseAdnDefaults(profile.profile).rateLimitFields),
      bidSpecificationFields: parseObjectiveFields(parseAdnDefaults(profile.profile).bidSpecificationFields),
      marketplaceBidSpecificationFields: parseObjectiveFields(parseAdnDefaults(profile.profile).marketplaceBidSpecificationFields),
      dynamicBiddingCustomEvents: biddingCustomEventIds(parseAdnDefaults(profile.profile)),
      orderObjectives: parseAdnDefaults(profile.profile).orderObjectives === true,
      feeTemplates: parseFeeTemplates(parseAdnDefaults(profile.profile)).map(feeTemplateToDraft),
      // Geospatial
      geospatialTargetCentreLat: network.geospatialTargetCentreLat ?? 59.325,
      geospatialTargetCentreLon: network.geospatialTargetCentreLon ?? 18.05,
      geospatialTargetZoom: network.geospatialTargetZoom ?? 10,
      // Field names (flat: key_name / key_abbreviation) — sourced from profile.adnDefaults.translations
      ...(() => {
        const adnDefaults = parseAdnDefaults(profile.profile)
        const translations = (adnDefaults.translations ?? {}) as Record<string, string>
        return Object.fromEntries(
          FIELD_NAME_KEYS.flatMap(({ key }) => {
            const map = TRANSLATION_KEY_MAP[key]
            return [
              [`fn_${key}_name`, translations[map.nameKey] ?? ''],
              [`fn_${key}_abbreviation`, translations[map.abbrKey] ?? ''],
            ]
          })
        )
      })(),
    },
    onSubmit: ({ value }) => {
      // Build translations object for profile.adnDefaults
      const existingAdnDefaults = parseAdnDefaults(profile.profile)
      const translations: Record<string, string> = {}
      for (const { key } of FIELD_NAME_KEYS) {
        const map = TRANSLATION_KEY_MAP[key]
        const name = (value as Record<string, unknown>)[`fn_${key}_name`] as string
        const abbr = (value as Record<string, unknown>)[`fn_${key}_abbreviation`] as string
        if (name) translations[map.nameKey] = name
        if (abbr) translations[map.abbrKey] = abbr
      }
      const updatedAdnDefaults = {
        ...existingAdnDefaults,
        translations,
        appSectionVisibility: value.appSectionVisibility ? 'SHOW_ALL' : 'HIDE_INEDITABLE',
        lineItemUserState: value.defaultLineItemState || undefined,
        marketplaceLineItemUserState: value.defaultMarketplaceLineItemState || undefined,
        cpm: { amount: value.defaultCpmAmount, currency: value.defaultCpmCurrency },
        showCpmFees: !value.hideCpmFees,
        showExternalReference: !value.hideLineItemExternalRef,
        iabCategoryOnLineItem: value.hideIabCategory ? 'HIDE' : 'SHOW',
        runReportsOptions: [
          ...(value.allowReachAnalysis ? ['REACH_ANALYSIS'] : []),
          ...(value.allowAdvertiserReports ? ['ADVERTISER_REPORTS'] : []),
        ],
        startDayCalendar: CALENDAR_DAY_NUMBER[value.calendarStartDay] ?? 1,
        deliveryCurve: value.defaultLineItemSmoothing === 'FRONTLOADED' ? 'FRONT_LOADED' : value.defaultLineItemSmoothing === 'FASTASPOSSIBLE' ? 'FAST_AS_POSSIBLE' : 'EVEN',
        marketplaceDeliveryCurve: value.defaultMarketplaceLineItemSmoothing === 'FRONTLOADED' ? 'FRONT_LOADED' : value.defaultMarketplaceLineItemSmoothing === 'FASTASPOSSIBLE' ? 'FAST_AS_POSSIBLE' : 'EVEN',
        defaultIgnoreMarketplaceTargeting: value.defaultMarketplaceTargetingOption === 'IGNORE_TARGETING_RULES',
        defaultBidStrategy: value.defaultBidStrategy,
        rateLimitScope: value.defaultRateLimitScope,
        rateLimitTranslations: value.rateLimitAlias || undefined,
        defaultCreativeName: value.defaultCreativeName || undefined,
        lineItemDeduplicationLevel: value.defaultCreativeDelivery || undefined,
        objectiveFields: toObjectiveFieldsObj(value.objectiveFields),
        marketplaceObjectiveFields: toObjectiveFieldsObj(value.marketplaceObjectiveFields),
        rateLimitFields: toObjectiveFieldsObj(value.rateLimitFields),
        bidSpecificationFields: toBidSpecObj(value.bidSpecificationFields, BID_SPEC_FIELD_OPTIONS),
        marketplaceBidSpecificationFields: toBidSpecObj(value.marketplaceBidSpecificationFields, MARKETPLACE_BID_SPEC_FIELD_OPTIONS),
        dynamicBiddingCustomEvents: value.dynamicBiddingCustomEvents,
        orderObjectives: value.orderObjectives,
        // Only super admins see the templates, so only they may rewrite them.
        ...(superAdmin
          ? { feeTemplates: value.feeTemplates.flatMap((d) => draftToFeeTemplate(d) ?? []) }
          : {}),
      }

      const input: NetworkInput = {
        name: value.name,
        defaultCurrency: value.defaultCurrency,
        availableCurrencies: value.availableCurrencies,
        defaultTier: value.defaultTierId ? { id: value.defaultTierId } : null,
        reportLogoUrl: value.reportLogoUrl || '',
        notificationsUrl: value.notificationsUrl || '',
        defaultCpmCostModel: value.defaultCpmCostModel,
        eCpmMethod: value.eCpmMethod,
        viewabilityMethod: value.viewabilityMethod,
        requiresUserConsent: value.requiresUserConsent,
        defaultLineItemDateSpan: value.defaultLineItemDateSpan,
        geospatialTargetCentreLat: value.geospatialTargetCentreLat,
        geospatialTargetCentreLon: value.geospatialTargetCentreLon,
        geospatialTargetZoom: value.geospatialTargetZoom,
      }
      mutation.mutate(input)
      profileMutation.mutate({
        name: network.name,
        profile: {
          adnDefaults: JSON.stringify(updatedAdnDefaults),
          adnUiLogo: value.logoUrl || '',
        },
      })
    },
  })

  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: (input: NetworkInput) => NetworksApi.update(network.id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['network', network.tagId], data)
      markClean()
      toast.success(t('common.saved'), { description: `${data.name} has been updated.`, duration: 3000 })
    },
    onError: (error) => {
      toast.error('Failed to save network', { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const profileMutation = useMutation({
    mutationFn: (payload: NetworkProfile) => NetworkProfilesApi.update(network.tagId, payload),
    onSuccess: (data) => setNetworkProfile(data),
    onError: (error) => {
      toast.error('Failed to save network profile', { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  return (
    <AppShell crumbs={[{ label: t('nav.admin'), href: '/admin' }, { label: t('nav.networkSettings') }]}>
      <UnsavedChangesDialog blocker={blocker} />
      <div className="ad-page">
        <ValidationWarnings warnings={network?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.network')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder="Network name"
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
            <p style={{ color: 'var(--ad-text-muted)', fontSize: 13 }}>
              ID: {network.id} &nbsp;·&nbsp; Tag: {network.tagId}
            </p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            const urlFields = ['logoUrl', 'reportLogoUrl', 'notificationsUrl'] as const
            const hasUrlError = urlFields.some((name) => validateUrl(form.getFieldValue(name) as string))
            const unnamedTemplate =
              superAdmin && form.getFieldValue('feeTemplates').some((d) => !d.name.trim())
            if (unnamedTemplate) toast.error(t('feeTemplates.nameRequired'))
            if (!hasUrlError && !unnamedTemplate) form.handleSubmit()
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 500px', gap: 16, alignItems: 'start' }}>
          {/* ── LEFT COLUMN ──────────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── General ─────────────────────────────────────────────────── */}
          <Card>
            <CardHeader><CardTitle><CardIcon icon={Globe} />General</CardTitle></CardHeader>
            <CardContent>
              <div className="field-grid">
                <div className="p-field">
                  <FieldLabel tooltip="The currency new objects default to. Limited to the network's available currencies.">Default currency</FieldLabel>
                  <form.Subscribe selector={(state) => state.values.availableCurrencies}>
                    {(availableCurrencies) => (
                      <form.Field name="defaultCurrency">
                        {(f) => (
                          <CurrencySelect
                            label="Default currency"
                            codes={availableCurrencies}
                            value={f.state.value}
                            onValueChange={(v) => f.handleChange(v)}
                            showNames
                            className="w-full"
                          />
                        )}
                      </form.Field>
                    )}
                  </form.Subscribe>
                </div>

                <div className="p-field">
                  <FieldLabel tooltip="The currencies objects on this network may be priced in.">Available currencies</FieldLabel>
                  <form.Field name="availableCurrencies">
                    {(f) => (
                      <MultiSelect
                        options={currencyOptions}
                        value={f.state.value}
                        onChange={(v) => f.handleChange(v)}
                        placeholder="Select currencies…"
                      />
                    )}
                  </form.Field>
                </div>

                {network.tiers.length > 0 && (
                  <div className="p-field">
                    <label>Default tier</label>
                    <form.Field name="defaultTierId">
                      {(f) => (
                        <Select label="Default tier" value={f.state.value} onValueChange={(v) => f.handleChange(v ?? '')}>
                          <SelectTrigger>
                            <SelectValue>{network.tiers.find((t) => t.id === f.state.value)?.name ?? 'None'}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">None</SelectItem>
                            {network.tiers.map((tier) => (
                              <SelectItem key={tier.id} value={tier.id}>{tier.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ── Appearance & notifications ───────────────────────────────── */}
          <Card>
            <CardHeader><CardTitle><CardIcon icon={Bell} />Appearance &amp; notifications</CardTitle></CardHeader>
            <CardContent>
              <div className="field-grid">
                <div className="p-field">
                  <label>User-interface logo URL</label>
                  <form.Field name="logoUrl" validators={{ onChange: ({ value }) => validateUrl(value) }}>
                    {(f) => <>
                      <Input value={f.state.value} onChange={(e) => f.handleChange(e.target.value)} placeholder="https://…" style={f.state.meta.errors.length ? { borderColor: 'var(--ad-danger)' } : undefined} />
                      {f.state.meta.errors[0] && <p style={{ color: 'var(--ad-danger)', fontSize: 12, marginTop: 4 }}>{f.state.meta.errors[0]}</p>}
                    </>}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Report logo URL</label>
                  <form.Field name="reportLogoUrl" validators={{ onChange: ({ value }) => validateUrl(value) }}>
                    {(f) => <>
                      <Input value={f.state.value} onChange={(e) => f.handleChange(e.target.value)} placeholder="https://…" style={f.state.meta.errors.length ? { borderColor: 'var(--ad-danger)' } : undefined} />
                      {f.state.meta.errors[0] && <p style={{ color: 'var(--ad-danger)', fontSize: 12, marginTop: 4 }}>{f.state.meta.errors[0]}</p>}
                    </>}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Web notifications URL</label>
                  <form.Field name="notificationsUrl" validators={{ onChange: ({ value }) => validateUrl(value) }}>
                    {(f) => <>
                      <Input value={f.state.value} onChange={(e) => f.handleChange(e.target.value)} placeholder="https://…" style={f.state.meta.errors.length ? { borderColor: 'var(--ad-danger)' } : undefined} />
                      {f.state.meta.errors[0] && <p style={{ color: 'var(--ad-danger)', fontSize: 12, marginTop: 4 }}>{f.state.meta.errors[0]}</p>}
                    </>}
                  </form.Field>
                </div>

                <div className="p-field">
                  <form.Field name="appSectionVisibility">
                    {(f) => <SwitchRow label="Show all app sections (including non-editable)" checked={f.state.value} onCheckedChange={(v) => f.handleChange(v)} />}
                  </form.Field>
                </div>

              </div>
            </CardContent>
          </Card>

          {/* ── Calculation methods ──────────────────────────────────────── */}
          <Card>
            <CardHeader><CardTitle><CardIcon icon={Eye} />Calculation methods</CardTitle></CardHeader>
            <CardContent>
              <div className="field-grid">
                {([
                  ['defaultCpmCostModel', 'CPM cost calculation method',      CPM_COST_MODELS,     'cpmCalculationMethod'],
                  ['eCpmMethod',          'eCPM calculation method',          ECPM_METHODS,        'ecpmCalculationMethod'],
                  ['viewabilityMethod',   'Viewability calculation method',   VIEWABILITY_METHODS, 'viewabilityCalculationMethod'],
                ] as const).map(([name, label, opts, tooltipKey]) => (
                  <div key={name} className="p-field">
                    <FieldLabel tooltip={tooltipKey ? t(`tooltips.${tooltipKey}`) : undefined}>
                      {label}
                    </FieldLabel>
                    <form.Field name={name}>
                      {(f) => (
                        <Select label={label} value={f.state.value} onValueChange={(v) => f.handleChange(v ?? '')}>
                          <SelectTrigger><SelectValue>{optionLabel(opts, f.state.value)}</SelectValue></SelectTrigger>
                          <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ── Privacy & data ───────────────────────────────────────────── */}
          <Card tone="attention">
            <CardHeader><CardTitle><CardIcon icon={Settings2} />Privacy &amp; data</CardTitle></CardHeader>
            <CardContent>
              <div className="field-grid">
                <div className="p-field">
                  <form.Field name="requiresUserConsent">
                    {(f) => <SwitchRow label="Consent required" checked={f.state.value} onCheckedChange={(v) => f.handleChange(v)} />}
                  </form.Field>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Line item defaults ───────────────────────────────────────── */}
          <Card tone="delivery">
            <CardHeader><CardTitle><CardIcon icon={List} />Line item defaults</CardTitle></CardHeader>
            <CardContent>
              <div className="field-grid">
                <div className="p-field">
                  <label>Default line item state</label>
                  <form.Field name="defaultLineItemState">
                    {(f) => (
                      <Select label="Default line item state" value={f.state.value} onValueChange={(v) => f.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue>{optionLabel(LINE_ITEM_STATES, f.state.value)}</SelectValue></SelectTrigger>
                        <SelectContent>{LINE_ITEM_STATES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Default marketplace line item state</label>
                  <form.Field name="defaultMarketplaceLineItemState">
                    {(f) => (
                      <Select label="Default marketplace line item state" value={f.state.value} onValueChange={(v) => f.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue>{optionLabel(LINE_ITEM_STATES, f.state.value)}</SelectValue></SelectTrigger>
                        <SelectContent>{LINE_ITEM_STATES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>

                <div className="p-field">
                  <FieldLabel tooltip={t('tooltips.lineItemDaysApart')}>Days apart on line item dates</FieldLabel>
                  <form.Field name="defaultLineItemDateSpan">
                    {(f) => <Input type="number" min={0} value={f.state.value} onChange={(e) => f.handleChange(Number(e.target.value))} style={{ width: 100 }} />}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Default CPM</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <form.Field name="defaultCpmAmount">
                      {(f) => <Input type="number" min={0} step={0.01} value={f.state.value} onChange={(e) => f.handleChange(Number(e.target.value))} style={{ width: 120 }} />}
                    </form.Field>
                    <form.Subscribe selector={(state) => state.values.availableCurrencies}>
                      {(availableCurrencies) => (
                        <form.Field name="defaultCpmCurrency">
                          {(f) => (
                            <CurrencySelect
                              codes={availableCurrencies}
                              value={f.state.value}
                              onValueChange={(v) => f.handleChange(v)}
                            />
                          )}
                        </form.Field>
                      )}
                    </form.Subscribe>
                  </div>
                </div>

                <div className="p-field">
                  <label>Line item smoothing</label>
                  <form.Field name="defaultLineItemSmoothing">
                    {(f) => (
                      <Select label="Line item smoothing" value={f.state.value} onValueChange={(v) => f.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue>{optionLabel(SMOOTHING_OPTIONS, f.state.value)}</SelectValue></SelectTrigger>
                        <SelectContent>{SMOOTHING_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Marketplace line item smoothing</label>
                  <form.Field name="defaultMarketplaceLineItemSmoothing">
                    {(f) => (
                      <Select label="Marketplace line item smoothing" value={f.state.value} onValueChange={(v) => f.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue>{optionLabel(SMOOTHING_OPTIONS, f.state.value)}</SelectValue></SelectTrigger>
                        <SelectContent>{SMOOTHING_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>

                <div className="p-field">
                  <FieldLabel tooltip={t('tooltips.defaultMarketplaceTargetingOption')}>Default marketplace targeting option</FieldLabel>
                  <form.Field name="defaultMarketplaceTargetingOption">
                    {(f) => (
                      <Select label="Default marketplace targeting option" value={f.state.value} onValueChange={(v) => f.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue>{optionLabel(MARKETPLACE_TARGETING_OPTIONS, f.state.value)}</SelectValue></SelectTrigger>
                        <SelectContent>{MARKETPLACE_TARGETING_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>

                <div className="p-field">
                  <FieldLabel tooltip={t('lineItems.defaultBidStrategyHelp')}>{t('lineItems.defaultBidStrategy')}</FieldLabel>
                  <form.Field name="defaultBidStrategy">
                    {(f) => (
                      <Select label={t('lineItems.defaultBidStrategy')} value={f.state.value} onValueChange={(v) => v && f.handleChange(v as BidStrategy)}>
                        <SelectTrigger><SelectValue>{t(`lineItems.bidStrategies.${f.state.value}`)}</SelectValue></SelectTrigger>
                        <SelectContent>{BID_STRATEGIES.map((s) => <SelectItem key={s} value={s}>{t(`lineItems.bidStrategies.${s}`)}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Default creative deduplication level</label>
                  <form.Field name="defaultCreativeDelivery">
                    {(f) => (
                      <Select label="Default creative deduplication level" value={f.state.value} onValueChange={(v) => f.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue>{optionLabel(CREATIVE_DELIVERY_OPTIONS, f.state.value)}</SelectValue></SelectTrigger>
                        <SelectContent>{CREATIVE_DELIVERY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Rate limit scope</label>
                  <form.Field name="defaultRateLimitScope">
                    {(f) => (
                      <Select label="Rate limit scope" value={f.state.value} onValueChange={(v) => f.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue>{optionLabel(RATE_LIMIT_SCOPES, f.state.value)}</SelectValue></SelectTrigger>
                        <SelectContent>{RATE_LIMIT_SCOPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Rate limit naming</label>
                  <form.Field name="rateLimitAlias">
                    {(f) => (
                      <Select label="Rate limit naming" value={f.state.value} onValueChange={(v) => f.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue>{optionLabel(RATE_LIMIT_TRANSLATIONS, f.state.value) || 'Select naming…'}</SelectValue></SelectTrigger>
                        <SelectContent>{RATE_LIMIT_TRANSLATIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Default creative name template</label>
                  <form.Field name="defaultCreativeName">
                    {(f) => <Input value={f.state.value} onChange={(e) => f.handleChange(e.target.value)} placeholder="Creative for {lineItemName}" />}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Start day on calendars</label>
                  <form.Field name="calendarStartDay">
                    {(f) => (
                      <Select label="Start day on calendars" value={f.state.value} onValueChange={(v) => f.handleChange(v ?? '')}>
                        <SelectTrigger><SelectValue>{optionLabel(CALENDAR_START_DAYS, f.state.value)}</SelectValue></SelectTrigger>
                        <SelectContent>{CALENDAR_START_DAYS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>

                <div className="p-field">
                  <form.Field name="hideCpmFees">
                    {(f) => <SwitchRow label="Hide CPM fees fields from line item page" checked={f.state.value} onCheckedChange={(v) => f.handleChange(v)} />}
                  </form.Field>
                </div>
                <div className="p-field">
                  <form.Field name="hideLineItemExternalRef">
                    {(f) => <SwitchRow label="Hide external reference field on line item page" checked={f.state.value} onCheckedChange={(v) => f.handleChange(v)} />}
                  </form.Field>
                </div>
                <div className="p-field">
                  <form.Field name="hideIabCategory">
                    {(f) => <SwitchRow label="Hide IAB category field on line item page" checked={f.state.value} onCheckedChange={(v) => f.handleChange(v)} />}
                  </form.Field>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Report permissions ───────────────────────────────────────── */}
          <Card tone="people">
            <CardHeader><CardTitle><CardIcon icon={Eye} />Report permissions</CardTitle></CardHeader>
            <CardContent>
              <div className="field-grid">
                <div className="p-field">
                  <form.Field name="allowReachAnalysis">
                    {(f) => <SwitchRow label="Allow running reach analysis" checked={f.state.value} onCheckedChange={(v) => f.handleChange(v)} />}
                  </form.Field>
                </div>
                <div className="p-field">
                  <form.Field name="allowAdvertiserReports">
                    {(f) => <SwitchRow label="Allow running advertiser reports" checked={f.state.value} onCheckedChange={(v) => f.handleChange(v)} />}
                  </form.Field>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Objectives ───────────────────────────────────────────────── */}
          <Card tone="delivery">
            <CardHeader><CardTitle><CardIcon icon={Target} />Objectives</CardTitle></CardHeader>
            <CardContent>
              <div className="field-grid">
                <div className="p-field">
                  <form.Field name="orderObjectives">
                    {(f) => <SwitchRow label="Allow budget and smoothing on orders" checked={f.state.value} onCheckedChange={(v) => f.handleChange(v)} />}
                  </form.Field>
                </div>
                <div className="p-field">
                  <label>Line item objective fields</label>
                  <form.Field name="objectiveFields">
                    {(f) => (
                      <MultiSelect
                        options={OBJECTIVE_FIELD_OPTIONS}
                        value={f.state.value}
                        onChange={(v) => f.handleChange(v)}
                        placeholder="Select fields…"
                      />
                    )}
                  </form.Field>
                </div>
                <div className="p-field">
                  <label>Marketplace objective fields</label>
                  <form.Field name="marketplaceObjectiveFields">
                    {(f) => (
                      <MultiSelect
                        options={OBJECTIVE_FIELD_OPTIONS}
                        value={f.state.value}
                        onChange={(v) => f.handleChange(v)}
                        placeholder="Select fields…"
                      />
                    )}
                  </form.Field>
                </div>
                <div className="p-field">
                  <label>Rate limit fields</label>
                  <form.Field name="rateLimitFields">
                    {(f) => (
                      <MultiSelect
                        options={OBJECTIVE_FIELD_OPTIONS}
                        value={f.state.value}
                        onChange={(v) => f.handleChange(v)}
                        placeholder="Select fields…"
                      />
                    )}
                  </form.Field>
                </div>
                <div className="p-field">
                  <label>Bid specification fields</label>
                  <form.Field name="bidSpecificationFields">
                    {(f) => (
                      <MultiSelect
                        options={BID_SPEC_FIELD_OPTIONS}
                        value={f.state.value}
                        onChange={(v) => f.handleChange(v)}
                        placeholder="Select fields…"
                      />
                    )}
                  </form.Field>
                </div>
                <div className="p-field">
                  <label>Marketplace bid specification fields</label>
                  <form.Field name="marketplaceBidSpecificationFields">
                    {(f) => (
                      <MultiSelect
                        options={MARKETPLACE_BID_SPEC_FIELD_OPTIONS}
                        value={f.state.value}
                        onChange={(v) => f.handleChange(v)}
                        placeholder="Select fields…"
                      />
                    )}
                  </form.Field>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Dynamic bidding ──────────────────────────────────────────── */}
          <Card tone="money">
            <CardHeader><CardTitle><CardIcon icon={Zap} />Dynamic bidding</CardTitle></CardHeader>
            <CardContent>
              <div className="field-grid">
                <div className="p-field full">
                  <FieldLabel tooltip="Custom events a line item with a dynamic bid strategy can choose as its bidding goal, next to impressions, clicks and views.">Custom events</FieldLabel>
                  <form.Field name="dynamicBiddingCustomEvents">
                    {(f) => (
                      <MultiSelect
                        // Inactive events stay listed only while still selected, so they can be removed.
                        options={customEvents
                          .filter((e) => e.objectState === 'ACTIVE' || f.state.value.includes(e.id))
                          .map((e) => ({ label: e.name, value: e.id }))}
                        value={f.state.value}
                        onChange={(v) => f.handleChange(v)}
                        placeholder="Select custom events…"
                      />
                    )}
                  </form.Field>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Geospatial targeting ─────────────────────────────────────── */}
          <Card tone="delivery">
            <CardHeader><CardTitle><CardIcon icon={Map} />Geospatial targeting</CardTitle></CardHeader>
            <CardContent>
              <div className="field-grid">
                <div className="p-field">
                  <label>Centre point latitude</label>
                  <form.Field name="geospatialTargetCentreLat">
                    {(f) => <Input type="number" step="any" value={f.state.value} onChange={(e) => f.handleChange(Number(e.target.value))} style={{ width: 140 }} />}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Centre point longitude</label>
                  <form.Field name="geospatialTargetCentreLon">
                    {(f) => <Input type="number" step="any" value={f.state.value} onChange={(e) => f.handleChange(Number(e.target.value))} style={{ width: 140 }} />}
                  </form.Field>
                </div>

                <div className="p-field">
                  <label>Default zoom level</label>
                  <form.Field name="geospatialTargetZoom">
                    {(f) => <Input type="number" min={1} max={20} value={f.state.value} onChange={(e) => f.handleChange(Number(e.target.value))} style={{ width: 100 }} />}
                  </form.Field>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Field name overrides ─────────────────────────────────────── */}
          <Card>
            <CardHeader><CardTitle><CardIcon icon={Type} />Field name overrides</CardTitle></CardHeader>
            <CardContent style={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--ad-border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600, color: 'var(--ad-text-muted)', fontSize: 12, width: '35%' }}>Field</th>
                    <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600, color: 'var(--ad-text-muted)', fontSize: 12 }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600, color: 'var(--ad-text-muted)', fontSize: 12, width: 140 }}>Abbreviation</th>
                  </tr>
                </thead>
                <tbody>
                  {FIELD_NAME_KEYS.map(({ key, label }, i) => (
                    <tr key={key} style={{ borderBottom: i < FIELD_NAME_KEYS.length - 1 ? '1px solid var(--ad-border)' : undefined }}>
                      <td style={{ padding: '6px 16px', color: 'var(--ad-text-muted)', whiteSpace: 'nowrap' }}>{label}</td>
                      <td style={{ padding: '4px 8px 4px 16px' }}>
                        <form.Field name={`fn_${key}_name` as never}>
                          {(f) => <Input value={(f.state.value as string)} onChange={(e) => f.handleChange(e.target.value as never)} placeholder={label} style={{ height: 30, fontSize: 13 }} />}
                        </form.Field>
                      </td>
                      <td style={{ padding: '4px 16px 4px 8px' }}>
                        <form.Field name={`fn_${key}_abbreviation` as never}>
                          {(f) => <Input value={(f.state.value as string)} onChange={(e) => f.handleChange(e.target.value as never)} placeholder="Abbrev." style={{ height: 30, fontSize: 13 }} />}
                        </form.Field>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {superAdmin && (
            <form.Field name="feeTemplates">
              {(field) => (
                <FeeTemplatesCard
                  value={field.state.value}
                  onChange={(v) => field.handleChange(v)}
                  currencies={feeCurrencies}
                />
              )}
            </form.Field>
          )}

          </div>{/* end left column */}

          {/* ── RIGHT COLUMN — Platform capabilities (read-only) ────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <CardHeader><CardTitle><CardIcon icon={Cpu} />Platform capabilities</CardTitle></CardHeader>
            <CardContent style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {([
                  [network.advertisingPlatform,       'Advertising platform'],
                  [network.dataPlatform,              'Data platform'],
                  [network.suiPlatform,               'Self-service UI platform'],
                  [network.marketplacePlatform,       'Marketplace platform'],
                  [network.hybridMarketplacePlatform, 'Hybrid marketplace platform'],
                  [network.politicalApp,              'Political app'],
                  [network.transcodeConfig?.enabled,  'Video transcoding'],
                  [network.advancedLogging,            'Ad server data export'],
                ] as [boolean | undefined, string][]).map(([enabled, label]) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: enabled ? 'var(--ad-text)' : 'var(--ad-text-faint)' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: enabled ? 'var(--ad-success)' : 'var(--ad-border)', flexShrink: 0 }} />
                    {label}
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 13, color: 'var(--ad-text-muted)' }}>
                <span style={{ fontWeight: 600, color: 'var(--ad-text)' }}>Time zone: </span>{network.timeZone}
              </div>

              {network.paymentProvider && network.paymentProvider !== 'NONE' && (
                <div style={{ fontSize: 13, color: 'var(--ad-text-muted)' }}>
                  <span style={{ fontWeight: 600, color: 'var(--ad-text)' }}>Payment provider: </span>
                  {optionLabel(PAYMENT_PROVIDERS, network.paymentProvider)}
                </div>
              )}

              {network.advancedLogging && (
                <div style={{ fontSize: 13, color: 'var(--ad-text-muted)', background: 'var(--ad-neutral-soft)', borderRadius: 6, padding: '8px 12px' }}>
                  Ad server logs can be downloaded at <code style={{ fontSize: 12 }}>sftp://{network.tagId}.localhost.data.adnuntius.com:8164</code>. Authenticate using your Adnuntius username and password.
                </div>
              )}

              {network.transcodeConfig?.enabled && network.transcodeConfig.outputs && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ad-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    Transcode outputs
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--ad-border)' }}>
                        {['Output', 'Type', 'Height', 'Max bitrate', 'Audio normalisation'].map((h) => (
                          <th key={h} style={{ textAlign: 'left', padding: '4px 12px 4px 0', fontWeight: 600, color: 'var(--ad-text-muted)', fontSize: 12 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(network.transcodeConfig.outputs).map(([key, raw]) => {
                        const o = raw as Record<string, unknown>
                        return (
                          <tr key={key} style={{ borderBottom: '1px solid var(--ad-border)' }}>
                            <td style={{ padding: '6px 12px 6px 0', fontWeight: 600 }}>{key}</td>
                            <td style={{ padding: '6px 12px 6px 0', color: 'var(--ad-text-muted)' }}>{String(o.type ?? '—')}</td>
                            <td style={{ padding: '6px 12px 6px 0', color: 'var(--ad-text-muted)' }}>{o.height != null ? `${o.height}p` : '—'}</td>
                            <td style={{ padding: '6px 12px 6px 0', color: 'var(--ad-text-muted)' }}>{o.maxBitrate != null ? `${Math.round(Number(o.maxBitrate) / 1000)} kbps` : '—'}</td>
                            <td style={{ padding: '6px 0', color: 'var(--ad-text-muted)' }}>{o.audioNormalisation != null ? (o.audioNormalisation ? 'Yes' : 'No') : '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          </div>{/* end right column */}
          </div>{/* end two-column grid */}

          {/* ── Save bar ─────────────────────────────────────────────────── */}
          <div className={`detail-savebar${isDirty || mutation.isPending || profileMutation.isPending ? ' is-visible' : ''}`}>
            <div className="detail-savebar__left">
              {(mutation.isPending || profileMutation.isPending) && (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('common.saving')}
                </>
              )}
            </div>
            <div className="detail-savebar__actions">
              <Button type="button" variant="ghost" disabled={mutation.isPending || profileMutation.isPending} onClick={() => form.reset()}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending || profileMutation.isPending}>
                <Check className="size-4" />{t('common.save')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </AppShell>
  )
}

// ─── Page wrapper ─────────────────────────────────────────────────────────────

export function NetworkEditPage() {
  const { t } = useTranslation()
  const { currentNetwork, loading } = useNetwork()

  // Edited only once fetched, never from the copy stored on the last visit.
  const { networkProfile: profile, isFresh, isError } = useNetworkProfile()

  if (loading || (!!currentNetwork && !isFresh && !isError)) {
    return (
      <AppShell crumbs={[{ label: t('nav.admin'), href: '/admin' }, { label: t('nav.networkSettings') }]}>
        <FormSkeleton />
      </AppShell>
    )
  }
  if (!currentNetwork || !profile || !isFresh) return null
  return <NetworkEditForm network={currentNetwork} profile={profile} />
}
